import MediaUploader from "app/components/MediaUploader";
import { actions } from "app/components/MediaUploader/redux/slice";
import { CreateAssetResponse } from "app/components/MediaUploader/types";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

type UploaderRenderProps = {
  isLoading: boolean;
  isDragging: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadedFile: string | null;
  triggerFileDialog: () => void;
};

interface Props {
  children?:
    | ((args: UploaderRenderProps) => React.ReactNode)
    | React.ReactElement
    | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onUploadSuccess: (data: CreateAssetResponse) => void;
  onLoadingChange?: (loading: boolean) => void;
  onFileChosen?: (file: File) => void;
}

const TicketAttachmentUpload: React.FC<Props> = ({
  children,
  fileInputRef,
  onUploadSuccess,
  onLoadingChange,
  onFileChosen,
}) => {
  const dispatch = useDispatch();
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [internalFileInputEl, setInternalFileInputEl] =
    useState<HTMLInputElement | null>(null);

  // Upload handler used by MediaUploader
  const onUpload = (file: File) => {
    if (onFileChosen) {
      onFileChosen(file);
      return;
    }
    const mimeType = file.type;
    const type = mimeType.split("/")[0];
    dispatch(
      actions.uploadMedia({
        media: {
          file,
          type,
          mimeType,
          name: file.name,
        },
        callback: onSuccess,
      }),
    );
  };

  const onSuccess = (data: CreateAssetResponse) => {
    setUploadedFile(data.url);
    onUploadSuccess(data);
  };

  return (
    <MediaUploader onUpload={onUpload} accept="*/*" validateFile={() => true}>
      {({
        isLoading,
        isDragging,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        fileInputRef: internalRef, // <- internal input ref coming from MediaUploader
      }) => {
        if (!onFileChosen) {
          onLoadingChange?.(isLoading);
        }

        // Keep local input element handy + sync to parent’s ref
        if (internalRef?.current !== internalFileInputEl) {
          const el = internalRef?.current ?? null;
          setInternalFileInputEl(el);
          // Expose to parent as well
          if (fileInputRef) {
            (fileInputRef as any).current = el;
          }
        }

        const passDown: UploaderRenderProps = {
          isLoading,
          isDragging,
          handleDragOver,
          handleDragLeave,
          handleDrop,
          fileInputRef: internalRef,
          uploadedFile,
          triggerFileDialog: () => internalRef?.current?.click(),
        };

        // 1) Preferred: children as function
        if (typeof children === "function") {
          return (children as (args: UploaderRenderProps) => React.ReactNode)(
            passDown,
          );
        }

        // 2) Fallback: clone element and inject props
        if (React.isValidElement(children)) {
          return React.cloneElement(children, passDown as any);
        }

        // 3) If no children provided, render nothing (or you can render a default UI)
        return null;
      }}
    </MediaUploader>
  );
};

export default TicketAttachmentUpload;
